import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  ArrowTrendingUpIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ReceiptPercentIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import { FireLoader } from "../../components/Loader";
import { showError } from "../../utils/alert";

const EMPTY = {
  summary: { received: 0, received_this_month: 0, paid_payments: 0, pending_payments: 0, failed_payments: 0, active_subscriptions: 0, active_plan_value: 0 },
  payments: [],
  pagination: { page: 1, pages: 1, total: 0 },
};

const money = (amount, currency = "SLE") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 2 }).format(Number(amount || 0));

const date = (value) => value
  ? new Date(value).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "Not paid";

const statusStyle = (status) => ({
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-600",
  cancelled: "bg-slate-100 text-slate-600",
  refunded: "bg-purple-50 text-purple-700",
}[status] || "bg-slate-100 text-slate-600");

export default function SuperAdminFinance() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/super-admin/finance", { params: { search, status, page, limit: 15 } });
      setData(response?.data?.data || EMPTY);
    } catch (error) {
      await showError({ title: "Unable to Load Finance", text: error?.response?.data?.message || "Finance records could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <FireLoader fullScreen size="lg" text="Loading..." />;

  const summary = data.summary || EMPTY.summary;
  const cards = [
    { label: "Confirmed income", value: money(summary.received), help: `${summary.paid_payments} successful payments`, icon: BanknotesIcon, style: "bg-emerald-50 text-emerald-600" },
    { label: "Income this month", value: money(summary.received_this_month), help: "Confirmed payments only", icon: ArrowTrendingUpIcon, style: "bg-blue-50 text-blue-600" },
    { label: "Pending payments", value: Number(summary.pending_payments || 0).toLocaleString(), help: "Not included in income", icon: ClockIcon, style: "bg-amber-50 text-amber-600" },
    { label: "Active plan value", value: money(summary.active_plan_value), help: `${summary.active_subscriptions} active subscriptions`, icon: ReceiptPercentIcon, style: "bg-purple-50 text-purple-600" },
  ];

  return (
    <div className="space-y-5">
      <Helmet><title>Finance | PawaSchool Super Admin</title></Helmet>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-2xl font-bold text-slate-900">Finance</h1><p className="mt-1 text-sm text-slate-400">Monitor subscription income and every school payment.</p></div>
        <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs text-slate-500 shadow-sm ring-1 ring-slate-200"><CheckCircleIcon className="h-4 w-4 text-emerald-500" /> Income includes confirmed payments only</div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, help, icon: Icon, style }) => (
          <article key={label} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3"><div><p className="text-xs font-semibold text-slate-400">{label}</p><p className="mt-1 text-2xl font-bold text-slate-800">{value}</p></div><span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${style}`}><Icon className="h-5 w-5" /></span></div>
            <p className="mt-3 text-xs text-slate-400">{help}</p>
          </article>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="relative min-w-[220px] flex-1"><MagnifyingGlassIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search school..." className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100" /></label>
          <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-primary-400">
            <option value="all">All payments</option><option value="paid">Paid</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="cancelled">Cancelled</option><option value="refunded">Refunded</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3"><h2 className="text-sm font-bold text-slate-800">Payment history</h2><p className="text-xs text-slate-400">{Number(data.pagination.total || 0).toLocaleString()} payment records</p></div>
        <div className="overflow-x-auto"><table className="w-full min-w-[900px]"><thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400"><tr><th className="px-4 py-3">School</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Provider</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Payment date</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{data.payments.length ? data.payments.map((item) => <tr key={item.id} className="hover:bg-slate-50"><td className="px-4 py-3"><p className="text-sm font-bold text-slate-800">{item.school_name}</p><p className="text-xs text-slate-400">{item.school_code || `School #${item.school_id}`}</p></td><td className="px-4 py-3 text-sm font-medium text-slate-600">{item.plan_name}</td><td className="px-4 py-3 text-sm font-bold text-slate-800">{money(item.amount, item.currency)}</td><td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${statusStyle(item.status)}`}>{item.status}</span></td><td className="px-4 py-3 text-sm text-slate-500">{item.payment_provider || "—"}</td><td className="max-w-[170px] truncate px-4 py-3 text-xs text-slate-500" title={item.provider_reference}>{item.provider_reference || "—"}</td><td className="px-4 py-3 text-sm text-slate-500">{date(item.paid_at || item.created_at)}</td></tr>) : <tr><td colSpan="7" className="p-12 text-center"><XCircleIcon className="mx-auto h-9 w-9 text-slate-300" /><p className="mt-2 text-sm text-slate-400">No payment records found.</p></td></tr>}</tbody>
        </table></div>
      </div>

      {data.pagination.pages > 1 && <div className="flex items-center justify-between"><p className="text-xs text-slate-400">Page {data.pagination.page} of {data.pagination.pages}</p><div className="flex gap-2"><button disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border bg-white p-2 disabled:opacity-40"><ChevronLeftIcon className="h-4 w-4" /></button><button disabled={page >= data.pagination.pages} onClick={() => setPage((value) => value + 1)} className="rounded-lg border bg-white p-2 disabled:opacity-40"><ChevronRightIcon className="h-4 w-4" /></button></div></div>}
    </div>
  );
}
